import { useState } from "react";
import { X, Calendar, Clock, User, Stethoscope, CalendarPlus } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import DateCalendar from "../../DateCalendar";

interface RebookTreatmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (data: any) => void;
  treatment?: {
    clientName: string;
    treatmentName: string;
    practitioner: string;
  };
}

const treatments = [
  { id: "1", name: "Hydrafacial", price: 120 },
  { id: "2", name: "Laser Hair Removal", price: 250 },
  { id: "3", name: "Chemical Peel", price: 180 },
  { id: "4", name: "PRP Injection", price: 350 },
  { id: "5", name: "Dermal Fillers", price: 280 },
  { id: "6", name: "Botox", price: 220 },
  { id: "7", name: "Microneedling", price: 200 },
  { id: "8", name: "Lip Filler", price: 300 },
];

const practitioners = [
  { id: "1", name: "Emma Taylor" },
  { id: "2", name: "Dr Saleema" },
  { id: "3", name: "Dr Harper" },
  { id: "4", name: "Sarah M." },
];

const intervals = [
  { value: "2weeks", label: "2 Weeks" },
  { value: "4weeks", label: "4 Weeks" },
  { value: "6weeks", label: "6 Weeks" },
  { value: "8weeks", label: "8 Weeks" },
  { value: "3months", label: "3 Months" },
  { value: "custom", label: "Custom Date" },
];

export default function RebookTreatmentModal({
  isOpen,
  onClose,
  onSave,
  treatment,
}: RebookTreatmentModalProps) {
  const [selectedInterval, setSelectedInterval] = useState("4weeks");
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() + 28); // Default 4 weeks
    return date;
  });
  const [selectedTime, setSelectedTime] = useState("10:00");
  const [selectedTreatment, setSelectedTreatment] = useState(
    treatments.find((t) => t.name === treatment?.treatmentName)?.id || "1"
  );
  const [selectedPractitioner, setSelectedPractitioner] = useState(
    practitioners.find((p) => p.name === treatment?.practitioner)?.id || "1"
  );
  const [notes, setNotes] = useState("");
  const [isDateOpen, setIsDateOpen] = useState(false);
  const [sendReminder, setSendReminder] = useState(true);

  const handleIntervalChange = (value: string) => {
    setSelectedInterval(value);
    const date = new Date();
    switch (value) {
      case "2weeks":
        date.setDate(date.getDate() + 14);
        break;
      case "4weeks":
        date.setDate(date.getDate() + 28);
        break;
      case "6weeks":
        date.setDate(date.getDate() + 42);
        break;
      case "8weeks":
        date.setDate(date.getDate() + 56);
        break;
      case "3months":
        date.setMonth(date.getMonth() + 3);
        break;
      default:
        // Custom - don't change date
        return;
    }
    setSelectedDate(date);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const handleSave = () => {
    const treatmentDetails = treatments.find((t) => t.id === selectedTreatment);
    const practitionerDetails = practitioners.find((p) => p.id === selectedPractitioner);

    onSave?.({
      client: treatment?.clientName,
      treatment: treatmentDetails,
      practitioner: practitionerDetails,
      date: selectedDate,
      time: selectedTime,
      notes,
      sendReminder,
    });
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setSelectedInterval("4weeks");
    const date = new Date();
    date.setDate(date.getDate() + 28);
    setSelectedDate(date);
    setSelectedTime("10:00");
    setNotes("");
    setSendReminder(true);
  };

  const selectedTreatmentDetails = treatments.find((t) => t.id === selectedTreatment);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-[#e8e0f0]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#f0ebf5]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#f0ebf5] flex items-center justify-center">
              <CalendarPlus className="w-5 h-5 text-[#704aaf]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#2d2d2d]">Rebook Treatment</h2>
              {treatment && (
                <p className="text-sm text-[#6b6b6b]">Follow-up for {treatment.clientName}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-[#f8f5fc] rounded-lg transition-colors text-[#6b6b6b] hover:text-[#3d3d3d]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Quick Interval Selection */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Schedule Follow-up
            </label>
            <div className="grid grid-cols-3 gap-2">
              {intervals.map((interval) => (
                <button
                  key={interval.value}
                  onClick={() => handleIntervalChange(interval.value)}
                  className={`py-2 px-3 text-sm font-medium rounded-lg transition-colors ${
                    selectedInterval === interval.value
                      ? "bg-[#704aaf] text-white"
                      : "bg-[#f8f5fc] text-[#704aaf] border border-[#e8e0f0] hover:bg-[#f0ebf5]"
                  }`}
                >
                  {interval.label}
                </button>
              ))}
            </div>
          </div>

          {/* Date & Time */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Date & Time
            </label>
            <div className="flex gap-3">
              {/* Date Picker */}
              <Popover open={isDateOpen} onOpenChange={setIsDateOpen}>
                <PopoverTrigger asChild>
                  <button className="flex-1 flex items-center gap-2 px-3 py-2.5 border border-[#e8e0f0] rounded-lg text-sm text-[#3d3d3d] hover:border-[#704aaf] transition-colors">
                    <Calendar className="w-4 h-4 text-[#704aaf]" />
                    <span className="truncate">{formatDate(selectedDate)}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[1200] bg-white border border-[#e8e0f0] shadow-xl rounded-xl">
                  <DateCalendar
                    selectedDate={selectedDate}
                    onSelectDate={(date) => {
                      setSelectedDate(date);
                      setSelectedInterval("custom");
                      setIsDateOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>

              {/* Time Input */}
              <div className="flex items-center gap-2 px-3 py-2.5 border border-[#e8e0f0] rounded-lg hover:border-[#704aaf] transition-colors">
                <Clock className="w-4 h-4 text-[#704aaf]" />
                <input
                  type="time"
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  className="text-sm text-[#3d3d3d] outline-none w-20"
                />
              </div>
            </div>
          </div>

          {/* Treatment Selection */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Treatment
            </label>
            <Select value={selectedTreatment} onValueChange={setSelectedTreatment}>
              <SelectTrigger className="w-full border-[#e8e0f0] h-[42px] text-sm hover:border-[#704aaf] transition-colors">
                <div className="flex items-center gap-2">
                  <Stethoscope className="w-4 h-4 text-[#704aaf]" />
                  <SelectValue placeholder="Select treatment" />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-white border border-[#e8e0f0] shadow-xl rounded-xl z-[1200]">
                {treatments.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    <div className="flex items-center justify-between w-full">
                      <span>{t.name}</span>
                      <span className="text-[#6b6b6b] ml-2">£{t.price}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedTreatmentDetails && (
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-[#6b6b6b]">Price:</span>
                <span className="font-semibold text-[#704aaf]">£{selectedTreatmentDetails.price.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* Practitioner Selection */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Practitioner
            </label>
            <Select value={selectedPractitioner} onValueChange={setSelectedPractitioner}>
              <SelectTrigger className="w-full border-[#e8e0f0] h-[42px] text-sm hover:border-[#704aaf] transition-colors">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-[#704aaf]" />
                  <SelectValue placeholder="Select practitioner" />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-white border border-[#e8e0f0] shadow-xl rounded-xl z-[1200]">
                {practitioners.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes for the follow-up appointment..."
              className="w-full px-3 py-2.5 border border-[#e8e0f0] rounded-lg text-sm text-[#3d3d3d] placeholder-[#9b9b9b] resize-none h-20 focus:outline-none focus:ring-2 focus:ring-[#e8e0f0] focus:border-[#704aaf] transition-all"
            />
          </div>

          {/* Send Reminder */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="send-reminder"
              checked={sendReminder}
              onChange={(e) => setSendReminder(e.target.checked)}
              className="w-4 h-4 rounded border-[#e8e0f0] text-[#704aaf] focus:ring-[#704aaf]"
            />
            <label htmlFor="send-reminder" className="text-sm text-[#5c5c5c]">
              Send appointment reminder to client
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-[#f0ebf5] bg-[#faf8fc]">
          <div className="flex items-center justify-between">
            {selectedTreatmentDetails && (
              <div className="text-sm">
                <span className="text-[#6b6b6b]">Total:</span>
                <span className="ml-2 text-lg font-bold text-[#2d2d2d]">
                  £{selectedTreatmentDetails.price.toFixed(2)}
                </span>
              </div>
            )}
            {!selectedTreatmentDetails && <div />}

            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-[#6b6b6b] hover:bg-[#f0ebf5] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#764BB7] transition-colors"
              >
                <CalendarPlus className="w-4 h-4" />
                Book Follow-up
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


